// WebGL2 points renderer for the Sound Map's dot layer.
// Positions/colours upload once; each pan/zoom frame is a single drawArrays call
// with the camera passed as uniforms — ~10x+ faster than CPU pixel-stamping and
// scales to millions of points. Falls back to null if WebGL2 is unavailable.

const VERT = `#version 300 es
layout(location=0) in vec2 a_pos;
layout(location=1) in vec4 a_color;
layout(location=2) in float a_hl;
uniform vec2 u_cam;
uniform float u_zoom;
uniform vec2 u_viewport;   // CSS px
uniform float u_size;      // device px diameter
uniform float u_hlMode;    // 1 = a highlight set is active
uniform float u_dim;       // alpha multiplier for non-highlighted points
uniform vec3 u_hlColor;    // highlighted points take the accent colour
out vec4 v_color;
void main() {
  vec2 screen = (a_pos - u_cam) * u_zoom + u_viewport * 0.5;
  vec2 clip = vec2(screen.x / u_viewport.x * 2.0 - 1.0, 1.0 - screen.y / u_viewport.y * 2.0);
  gl_Position = vec4(clip, 0.0, 1.0);
  gl_PointSize = u_size;
  if (u_hlMode > 0.5) {
    v_color = (a_hl > 0.5) ? vec4(u_hlColor, 1.0) : vec4(a_color.rgb, a_color.a * u_dim);
  } else {
    v_color = a_color;
  }
}`

const FRAG = `#version 300 es
precision mediump float;
in vec4 v_color;
out vec4 frag;
void main() {
  vec2 d = gl_PointCoord - 0.5;
  if (dot(d, d) > 0.25) discard;   // round dot
  frag = vec4(v_color.rgb * v_color.a, v_color.a);  // premultiplied
}`

export interface PointsGLDraw {
  cx: number; cy: number; zoom: number
  W: number; H: number
  size: number          // dot diameter in device px
  hlMode: boolean
  dim: number           // 0..1
  hlColor: [number, number, number]  // 0..1 RGB
}

function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const s = gl.createShader(type)!
  gl.shaderSource(s, src); gl.compileShader(s)
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(s)
    gl.deleteShader(s)
    throw new Error('shader compile: ' + log)
  }
  return s
}

export class PointsGL {
  private gl: WebGL2RenderingContext
  private prog: WebGLProgram
  private vao: WebGLVertexArrayObject
  private posBuf: WebGLBuffer
  private colBuf: WebGLBuffer
  private hlBuf: WebGLBuffer
  private u: Record<string, WebGLUniformLocation | null> = {}
  private count = 0
  private hlCapacity = 0

  constructor(canvas: HTMLCanvasElement) {
    const gl = canvas.getContext('webgl2', { antialias: true, premultipliedAlpha: true, alpha: true })
    if (!gl) throw new Error('no webgl2')
    this.gl = gl
    const vs = compile(gl, gl.VERTEX_SHADER, VERT)
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG)
    const prog = gl.createProgram()!
    gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog)
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error('link: ' + gl.getProgramInfoLog(prog))
    this.prog = prog
    gl.deleteShader(vs); gl.deleteShader(fs)
    for (const name of ['u_cam', 'u_zoom', 'u_viewport', 'u_size', 'u_hlMode', 'u_dim', 'u_hlColor']) {
      this.u[name] = gl.getUniformLocation(prog, name)
    }
    this.vao = gl.createVertexArray()!
    this.posBuf = gl.createBuffer()!
    this.colBuf = gl.createBuffer()!
    this.hlBuf = gl.createBuffer()!
    gl.bindVertexArray(this.vao)
    gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuf)
    gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)
    gl.bindBuffer(gl.ARRAY_BUFFER, this.colBuf)
    gl.enableVertexAttribArray(1); gl.vertexAttribPointer(1, 4, gl.UNSIGNED_BYTE, true, 0, 0)
    gl.bindBuffer(gl.ARRAY_BUFFER, this.hlBuf)
    gl.enableVertexAttribArray(2); gl.vertexAttribPointer(2, 1, gl.UNSIGNED_BYTE, false, 0, 0)
    gl.bindVertexArray(null)
    gl.enable(gl.BLEND)
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)  // premultiplied
  }

  setPositions(px: Float32Array, py: Float32Array) {
    const n = px.length
    const xy = new Float32Array(n * 2)
    for (let i = 0; i < n; i++) { xy[i * 2] = px[i]; xy[i * 2 + 1] = py[i] }
    const gl = this.gl
    gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuf)
    gl.bufferData(gl.ARRAY_BUFFER, xy, gl.STATIC_DRAW)
    this.count = n
    // keep highlight buffer sized to point count (default 0 = none highlighted)
    if (this.hlCapacity < n) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.hlBuf)
      gl.bufferData(gl.ARRAY_BUFFER, new Uint8Array(n), gl.DYNAMIC_DRAW)
      this.hlCapacity = n
    }
  }

  setColors(colorsU32: Uint32Array) {
    const gl = this.gl
    const bytes = new Uint8Array(colorsU32.buffer, colorsU32.byteOffset, colorsU32.length * 4)
    gl.bindBuffer(gl.ARRAY_BUFFER, this.colBuf)
    gl.bufferData(gl.ARRAY_BUFFER, bytes, gl.STATIC_DRAW)
  }

  setHighlight(indices: number[] | null) {
    const gl = this.gl
    const flags = new Uint8Array(Math.max(this.count, 1))
    if (indices) for (let i = 0; i < indices.length; i++) flags[indices[i]] = 1
    gl.bindBuffer(gl.ARRAY_BUFFER, this.hlBuf)
    gl.bufferData(gl.ARRAY_BUFFER, flags, gl.DYNAMIC_DRAW)
    this.hlCapacity = flags.length
  }

  resize(deviceW: number, deviceH: number) {
    this.gl.viewport(0, 0, deviceW, deviceH)
  }

  draw(p: PointsGLDraw) {
    const gl = this.gl
    gl.clearColor(0, 0, 0, 0)
    gl.clear(gl.COLOR_BUFFER_BIT)
    if (this.count === 0) return
    gl.useProgram(this.prog)
    gl.bindVertexArray(this.vao)
    gl.uniform2f(this.u.u_cam!, p.cx, p.cy)
    gl.uniform1f(this.u.u_zoom!, p.zoom)
    gl.uniform2f(this.u.u_viewport!, p.W, p.H)
    gl.uniform1f(this.u.u_size!, Math.max(1, p.size))
    gl.uniform1f(this.u.u_hlMode!, p.hlMode ? 1 : 0)
    gl.uniform1f(this.u.u_dim!, p.dim)
    gl.uniform3f(this.u.u_hlColor!, ...p.hlColor)
    gl.drawArrays(gl.POINTS, 0, this.count)
    gl.bindVertexArray(null)
  }

  dispose() {
    const gl = this.gl
    gl.deleteBuffer(this.posBuf); gl.deleteBuffer(this.colBuf); gl.deleteBuffer(this.hlBuf)
    gl.deleteVertexArray(this.vao); gl.deleteProgram(this.prog)
  }
}

export function createPointsGL(canvas: HTMLCanvasElement): PointsGL | null {
  try { return new PointsGL(canvas) } catch (e) { console.warn('[map] WebGL2 unavailable, falling back to 2D:', e); return null }
}
